import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Edit, Trash2, BookOpen, Plus } from "lucide-react";
import { API, COVER_IMAGE } from "../lib/constants";
import { useUser } from "../contexts/UserContext";
import WitnessStandTabs from "../components/WitnessStandTabs";

const STATUS_COLORS = {
  draft: "bg-gray-500/15 text-gray-300 border-gray-500/30",
  pending: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  approved: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  rejected: "bg-red-500/15 text-red-300 border-red-500/30",
};

export default function WitnessStandMyStories() {
  const navigate = useNavigate();
  const { user } = useUser();
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    const token = localStorage.getItem("user_session_token");
    fetch(`${API}/witness-stand/my-stories`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: "include",
    })
      .then(r => r.ok ? r.json() : { stories: [] })
      .then(d => { setStories(d.stories || []); })
      .catch(() => setStories([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { if (user) load(); else setLoading(false); }, [user]);

  const onDelete = async (id) => {
    if (!window.confirm("Delete this story permanently?")) return;
    const token = localStorage.getItem("user_session_token");
    await fetch(`${API}/witness-stand/stories/${id}`, {
      method: "DELETE",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: "include",
    });
    load();
  };

  if (!user) return (
    <div className="min-h-screen pt-24 pb-20 px-6" data-testid="mystories-need-auth">
      <div className="max-w-4xl mx-auto">
        <WitnessStandTabs />
        <div className="text-center py-16">
          <p className="text-gray-400 mb-4">Please sign in to see your stories.</p>
          <button onClick={() => navigate("/witness-stand")} className="px-4 py-2 bg-red-600 rounded-lg text-white font-bold text-sm">Back to Library</button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen pt-24 pb-20 px-6" data-testid="my-stories-page">
      <div className="max-w-4xl mx-auto">
        <WitnessStandTabs />
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div>
            <p className="font-mono text-red-500 text-xs tracking-[0.3em] mb-1">YOUR DRAFTS & PUBLISHED</p>
            <h1 className="font-cinzel text-2xl md:text-3xl font-bold">My Stories</h1>
          </div>
          <button onClick={() => navigate("/witness-stand/write")} className="flex items-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 rounded-lg text-sm font-bold text-white" data-testid="new-story-btn">
            <Plus size={14}/>New Story
          </button>
        </div>

        {loading ? <p className="text-center text-gray-500 py-12 font-mono text-xs">LOADING…</p> :
         stories.length === 0 ? (
          <div className="text-center py-16 border border-white/5 rounded-xl bg-[#0a0a0a]" data-testid="mystories-empty">
            <BookOpen size={32} className="text-red-500/40 mx-auto mb-3"/>
            <p className="text-gray-400 text-sm mb-4">You haven't started any stories yet.</p>
            <button onClick={() => navigate("/witness-stand/write")} className="px-5 py-2.5 bg-red-600 hover:bg-red-700 rounded-lg text-sm font-bold text-white">Start Your First Story</button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="mystories-list">
            {stories.map((s) => (
              <div key={s.id} className="flex flex-col sm:flex-row gap-3 p-3 bg-[#0a0a0a] border border-white/10 rounded-xl" data-testid={`mystory-row-${s.id}`}>
                <div className="w-full sm:w-32 h-24 bg-cover bg-center rounded-lg flex-shrink-0" style={{ backgroundImage: `url(${s.cover_url || COVER_IMAGE})` }}/>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className={`text-[10px] px-2 py-0.5 rounded border font-mono ${STATUS_COLORS[s.status] || ""}`}>{s.status?.toUpperCase()}</span>
                    <span className="text-[10px] font-mono text-gray-500">{s.genre}</span>
                    <span className="text-[10px] font-mono text-gray-600">· {s.word_count} WORDS</span>
                  </div>
                  <p className="font-cinzel font-bold text-white text-base truncate">{s.title || "(untitled)"}</p>
                  {s.subtitle && <p className="text-xs text-gray-400 line-clamp-1">{s.subtitle}</p>}
                  {s.moderator_note && s.status === "rejected" && (
                    <p className="mt-1 text-xs text-red-300 font-mono">REVIEWER: {s.moderator_note}</p>
                  )}
                  <div className="flex flex-wrap gap-1 mt-2">
                    {s.status === "approved" && (
                      <button onClick={() => navigate(`/witness-stand/story/${s.id}`)} className="px-3 py-1 rounded bg-white/5 hover:bg-white/10 text-[11px] text-gray-300" data-testid={`mystory-view-${s.id}`}>View</button>
                    )}
                    {(s.status === "draft" || s.status === "rejected") && (
                      <button onClick={() => navigate(`/witness-stand/write/${s.id}`)} className="flex items-center gap-1 px-3 py-1 rounded bg-white/5 hover:bg-white/10 text-[11px] text-gray-300" data-testid={`mystory-edit-${s.id}`}>
                        <Edit size={10}/>Edit
                      </button>
                    )}
                    {(s.status === "draft" || s.status === "rejected") && (
                      <button onClick={() => onDelete(s.id)} className="flex items-center gap-1 px-3 py-1 rounded bg-white/5 hover:bg-red-500/20 text-[11px] text-gray-300 hover:text-red-300" data-testid={`mystory-delete-${s.id}`}>
                        <Trash2 size={10}/>Delete
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
